/*
 * Dhrohar hero (0.4.0): full-width paired-photo slideshow with calm, desktop-only autoplay.
 * Adapted from Dawn's slideshow semantics and the W3C APG carousel pattern (no slider library):
 * - One setTimeout at a time; cleared on pause, before each reschedule and on disconnect.
 * - Autoplay needs 2+ slides and a Pause/Play button. Desktop (990px+) follows data-autoplay; phones and tablets
 *   follow data-autoplay-mobile (off by default). It never starts by itself with reduced motion or in the theme
 *   editor; a shopper's explicit Play still works there.
 * - Paused while a mouse hovers the hero, while focus is inside it (except on the Pause/Play button itself),
 *   while the tab is hidden and while a theme-editor block is selected.
 * - Any shopper slide change (previous / next, a progress bar, arrow keys, a swipe) stops autoplay for good, exactly
 *   like Pause. An explicit Pause sticks until Play is pressed; hover-out and focus-out do not resume it.
 * - The status live region is aria-live="off" while rotating and "polite" otherwise.
 * Written as a custom element so a theme-editor section reload creates a fresh element and the old one cleans up.
 */
if (!customElements.get('dhrohar-hero')) {
  class DhroharHero extends HTMLElement {
    constructor() {
      super();
      this.currentIndex = 0;
      this.timer = null;
      this.controller = null;
      this.primeLoadListener = null;
      this.primeIdleCallback = null;
      this.primeTimeout = null;
      this.playing = false; // the shopper's intent, shown on the Pause/Play button
      this.userChose = false; // set once the shopper presses Pause/Play or changes slide; the viewport no longer decides
      this.hovering = false;
      this.focusWithin = false;
      // An explicit Play press runs the slides while the input that pressed it stays on the toggle:
      // keyboard focus on the toggle and/or the pointer over it. Each part ends only when that input moves away.
      this.overrideFocus = false;
      this.overrideHover = false;
      this.editorHold = false;
      this.touch = null;
    }

    connectedCallback() {
      this.teardown();
      this.slides = Array.from(this.querySelectorAll('.dh-slide'));
      this.bars = Array.from(this.querySelectorAll('[data-dhrohar-hero-bar]'));
      this.status = this.querySelector('[data-dhrohar-hero-status]');
      this.toggle = this.querySelector('[data-dhrohar-hero-toggle]');
      this.track = this.querySelector('.dh-hero__track');
      const active = this.slides.findIndex((slide) => slide.classList.contains('is-active'));
      this.currentIndex = active > 0 ? active : 0;
      if (this.slides.length < 2) return;

      this.classList.add('is-enhanced');
      this.schedulePrime();
      this.controller = new AbortController();
      const opts = { signal: this.controller.signal };
      const passive = { signal: this.controller.signal, passive: true };

      this.addEventListener('click', (event) => this.onClick(event), opts);
      this.addEventListener('keydown', (event) => this.onKeydown(event), opts);
      this.addEventListener('focusin', (event) => this.onFocusIn(event), opts);
      this.addEventListener('focusout', (event) => this.onFocusOut(event), opts);
      this.addEventListener('pointerover', (event) => this.onPointerOver(event), opts);
      this.addEventListener('pointerleave', (event) => this.onPointerLeave(event), opts);
      if (this.track) {
        this.track.addEventListener('touchstart', (event) => this.onTouchStart(event), passive);
        this.track.addEventListener('touchend', (event) => this.onTouchEnd(event), passive);
        this.track.addEventListener('touchcancel', () => (this.touch = null), passive);
      }
      document.addEventListener('visibilitychange', () => this.refresh(), opts);
      // Theme editor: show the selected block's slide and hold it there.
      document.addEventListener('shopify:block:select', (event) => this.onBlockSelect(event), opts);
      document.addEventListener('shopify:block:deselect', (event) => this.onBlockDeselect(event), opts);

      this.motionQuery = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;
      if (this.motionQuery && this.motionQuery.addEventListener) {
        this.motionQuery.addEventListener('change', () => this.onMotionChange(), opts);
      }
      this.desktopQuery = window.matchMedia ? window.matchMedia('(min-width: 990px)') : null;
      if (this.desktopQuery && this.desktopQuery.addEventListener) {
        this.desktopQuery.addEventListener('change', () => this.onViewportChange(), opts);
      }

      // Autoplay starts by itself only when allowed by the merchant (per viewport), the shopper's motion setting and
      // outside the editor.
      this.userChose = false;
      this.playing = this.autoStartAllowed();
      this.focusWithin = this.contains(document.activeElement);
      this.updateToggle();
      this.refresh();
    }

    // Let the other slides' photos load only after the page itself has loaded, so slide 1's photos (LCP) have the network.
    schedulePrime() {
      this.cancelPrime();
      if (!this.isConnected || this.classList.contains('is-primed')) return;
      const later = () => {
        this.primeLoadListener = null;
        if (!this.isConnected) return;
        if (window.requestIdleCallback && window.cancelIdleCallback) {
          this.primeIdleCallback = window.requestIdleCallback(() => {
            this.primeIdleCallback = null;
            this.prime();
          }, { timeout: 2000 });
        } else {
          this.primeTimeout = window.setTimeout(() => {
            this.primeTimeout = null;
            this.prime();
          }, 200);
        }
      };
      if (document.readyState === 'complete') later();
      else {
        this.primeLoadListener = later;
        window.addEventListener('load', later, { once: true });
      }
    }

    cancelPrime() {
      if (this.primeLoadListener) {
        window.removeEventListener('load', this.primeLoadListener);
        this.primeLoadListener = null;
      }
      if (this.primeIdleCallback !== null) {
        window.cancelIdleCallback(this.primeIdleCallback);
        this.primeIdleCallback = null;
      }
      if (this.primeTimeout !== null) {
        window.clearTimeout(this.primeTimeout);
        this.primeTimeout = null;
      }
    }

    prime() {
      // A shopper can request a slide before load/idle; that immediate prime replaces the pending work.
      this.cancelPrime();
      if (!this.isConnected || this.classList.contains('is-primed')) return;
      this.classList.add('is-primed');
      // Both photos of every later slide: fetch them now at low priority instead of waiting on the lazy-load
      // visibility check, so a change never shows an empty frame. (On phones photo 2 resolves to a 1×1 data image.)
      this.querySelectorAll('.dh-slide__media img[loading="lazy"]').forEach((img) => {
        img.fetchPriority = 'low';
        img.loading = 'eager';
      });
    }

    disconnectedCallback() {
      this.teardown();
    }

    teardown() {
      this.cancelPrime();
      this.stopTimer();
      if (this.controller) this.controller.abort();
      this.controller = null;
    }

    /* ---------- public API (also used by the interaction tests) ---------- */

    play() {
      if (!this.autoplayAllowed()) return;
      this.playing = true;
      this.updateToggle();
      this.refresh();
    }

    pause() {
      this.playing = false;
      this.overrideFocus = false;
      this.overrideHover = false;
      this.updateToggle();
      this.refresh();
    }

    get speed() {
      const seconds = Number(this.dataset.speed);
      return (Number.isFinite(seconds) && seconds > 0 ? seconds : 7) * 1000;
    }

    get isRunning() {
      return this.timer !== null;
    }

    /* ---------- state ---------- */

    isDesktop() {
      return !this.desktopQuery || this.desktopQuery.matches;
    }

    // The merchant allows autoplay for the current viewport (desktop and phones have separate settings).
    autoplayAllowed() {
      if (!this.toggle || this.slides.length < 2) return false;
      return (this.isDesktop() ? this.dataset.autoplay : this.dataset.autoplayMobile) === 'true';
    }

    autoStartAllowed() {
      return this.autoplayAllowed() && !this.prefersReducedMotion() && !this.isDesignMode();
    }

    prefersReducedMotion() {
      return !!(this.motionQuery && this.motionQuery.matches);
    }

    isDesignMode() {
      return !!(window.Shopify && window.Shopify.designMode);
    }

    isToggle(element) {
      return !!(this.toggle && element && this.toggle.contains(element));
    }

    shouldRun() {
      return (
        this.playing &&
        this.autoplayAllowed() &&
        // Each explicit-Play part only cancels its own kind of hold, so a fresh hover still pauses a focus-kept Play.
        (this.overrideFocus || !this.focusWithin) &&
        (this.overrideHover || !this.hovering) &&
        !this.editorHold &&
        !document.hidden &&
        this.isConnected
      );
    }

    refresh() {
      if (this.shouldRun()) {
        if (!this.timer) {
          this.setLive('off');
          this.schedule();
        }
      } else {
        this.stopTimer();
        this.setLive('polite');
      }
    }

    schedule() {
      if (this.timer !== null) window.clearTimeout(this.timer);
      this.timer = null;
      if (!this.shouldRun()) {
        this.stopTimer();
        return;
      }
      const speed = this.speed;
      this.style.setProperty('--dh-hero-speed', `${speed}ms`);
      this.classList.add('is-running');
      this.timer = window.setTimeout(() => {
        this.timer = null;
        this.goTo(this.currentIndex + 1, { auto: true });
        // Calm by default: after the configured number of full rounds, rest on the first slide (like a manual Pause).
        this.autoAdvances = (this.autoAdvances || 0) + 1;
        const rounds = Number(this.dataset.rounds) || 0;
        if (rounds > 0 && this.autoAdvances >= rounds * this.slides.length && this.currentIndex === 0) {
          this.pause();
          return;
        }
        this.schedule();
      }, speed);
    }

    stopTimer() {
      if (this.timer !== null) window.clearTimeout(this.timer);
      this.timer = null;
      this.classList.remove('is-running');
    }

    setLive(value) {
      if (this.status && this.status.getAttribute('aria-live') !== value) this.status.setAttribute('aria-live', value);
    }

    updateToggle() {
      if (!this.toggle) return;
      const label = this.playing ? this.toggle.dataset.labelPause : this.toggle.dataset.labelPlay;
      if (label) this.toggle.setAttribute('aria-label', label);
      this.toggle.classList.toggle('is-paused', !this.playing);
    }

    /* ---------- events ---------- */

    onClick(event) {
      const target = event.target.closest('button');
      if (!target || !this.contains(target)) return;

      if (target.hasAttribute('data-dhrohar-hero-toggle')) {
        this.userChose = true;
        if (this.playing) {
          this.pause();
        } else {
          this.overrideFocus = this.isToggle(document.activeElement);
          this.overrideHover = this.hovering;
          // Keyboard activation on a toggle that somehow lacks focus still counts as an explicit Play.
          if (!this.overrideFocus && !this.overrideHover) this.overrideFocus = true;
          this.play();
        }
      } else if (target.hasAttribute('data-dhrohar-hero-prev')) {
        this.goTo(this.currentIndex - 1);
      } else if (target.hasAttribute('data-dhrohar-hero-next')) {
        this.goTo(this.currentIndex + 1);
      } else if (target.hasAttribute('data-dhrohar-hero-bar')) {
        this.goTo(Number(target.getAttribute('data-dhrohar-hero-bar')));
      }
    }

    onKeydown(event) {
      // Arrow keys only while focus is on the slide controls, so page scrolling is untouched.
      if (!event.target.closest('.dh-hero__controls')) return;
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.goTo(this.currentIndex - 1);
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.goTo(this.currentIndex + 1);
      }
    }

    onFocusIn(event) {
      // Any focus inside the hero holds the slides, including Tab onto Pause/Play: focus alone never restarts them.
      // Only an explicit Play press runs them while the toggle keeps focus.
      this.focusWithin = true;
      if (!this.isToggle(event.target)) {
        // Focus moved to another control: a new hold, so the explicit Play exception ends.
        this.overrideFocus = false;
        this.overrideHover = false;
      }
      this.refresh();
    }

    onFocusOut(event) {
      if (event.relatedTarget && this.contains(event.relatedTarget)) return; // the next focusin decides
      this.focusWithin = false;
      this.overrideFocus = false;
      this.refresh();
    }

    onPointerOver(event) {
      if (event.pointerType === 'touch') return; // touch taps are not hover
      // 0.5.1: only a pointer resting on something clickable (button, arrows, links) holds the slides. A mouse simply
      // lying over the photos no longer freezes the slideshow (shoppers read that as "not changing").
      this.hovering = !!(event.target.closest && event.target.closest('a, button'));
      if (!this.isToggle(event.target)) this.overrideHover = false;
      this.refresh();
    }

    onPointerLeave(event) {
      if (event.pointerType === 'touch') return;
      this.hovering = false;
      this.overrideHover = false; // pointer departure ends only the pointer's part of an explicit Play
      this.refresh();
    }

    onTouchStart(event) {
      const point = event.touches && event.touches[0];
      this.touch = point ? { x: point.clientX, y: point.clientY } : null;
    }

    onTouchEnd(event) {
      const point = event.changedTouches && event.changedTouches[0];
      if (!this.touch || !point) return;
      const dx = point.clientX - this.touch.x;
      const dy = point.clientY - this.touch.y;
      this.touch = null;
      // Only a clearly horizontal swipe changes slides; vertical scrolling is never blocked (listeners are passive).
      if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy) * 1.5) {
        this.goTo(this.currentIndex + (dx < 0 ? 1 : -1));
      }
    }

    onMotionChange() {
      // Turning reduced motion on stops autoplay; turning it off does not start it again by itself.
      if (this.prefersReducedMotion() && this.playing) this.pause();
    }

    onViewportChange() {
      // Crossing 990px (e.g. rotating a tablet) applies that viewport's autoplay setting, unless the shopper has
      // already chosen (Pause, Play or a slide change): their choice stands.
      if (!this.autoplayAllowed()) {
        if (this.playing) this.pause();
        return;
      }
      if (!this.userChose && !this.playing && this.autoStartAllowed()) this.play();
    }

    onBlockSelect(event) {
      const slide = this.slides.find((item) => item === event.target || item.contains(event.target));
      if (!slide) return;
      this.editorHold = true;
      this.goTo(Number(slide.dataset.index), { silent: true });
      this.refresh();
    }

    onBlockDeselect(event) {
      if (!this.slides.some((item) => item === event.target || item.contains(event.target))) return;
      this.editorHold = false;
      this.refresh();
    }

    /* ---------- slide change ---------- */

    goTo(index, { auto = false, silent = false } = {}) {
      const count = this.slides.length;
      if (count < 2) return;
      const nextIndex = ((index % count) + count) % count;

      // A shopper's own change stops autoplay for good, like Pause (Baymard / W3C APG); Play restarts it.
      if (!auto && !silent) {
        this.userChose = true;
        if (this.playing) this.pause();
      }

      this.currentIndex = nextIndex;
      this.prime();

      this.slides.forEach((slide, i) => {
        const active = i === nextIndex;
        slide.classList.toggle('is-active', active);
        if (active) {
          slide.removeAttribute('aria-hidden');
          slide.removeAttribute('inert');
        } else {
          slide.setAttribute('aria-hidden', 'true');
          slide.setAttribute('inert', '');
        }
      });

      this.bars.forEach((bar, i) => {
        if (i === nextIndex) bar.setAttribute('aria-current', 'true');
        else bar.removeAttribute('aria-current');
      });

      if (this.status) {
        // Automatic advances are not announced; shopper-initiated changes are.
        this.setLive(auto || silent ? 'off' : 'polite');
        this.status.textContent = `${nextIndex + 1} / ${count}`;
      }
    }
  }

  customElements.define('dhrohar-hero', DhroharHero);
}
